<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import type { CreativesLinkTableDetailsItem } from '@modules/creatives-link/submodules/types';
import type { ITableColumnsOptions } from '@modules/tables/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VButton from '@components/buttons/VButton/VButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import VDialog from '@components/dialogs/VDialog/VDialog.vue';
import { useCopyToClipboard } from '@modules/core/composables/copy';
import { useDevice } from '@modules/core/composables/device';
import { useDownloadImageByUrl } from '@modules/core/composables/download-image-by-url';
import {
  AFFILIATE_CREATIVES_LINK_ROUTE_NAME,
  CREATIVES_LINK_PERMISSIONS,
} from '@modules/creatives-link/constants';
import { TRANSLATION_GROUP_CREATIVES_LINK_TABLE } from '@modules/creatives-link/submodules/constants';
import { CreativeLinkHandlerEvents } from '@modules/creatives-link/submodules/enums';
import { useCreativeLinkListQuery } from '@modules/creatives-link/submodules/queries';
import VFormTextarea from '@modules/forms/components/inputs/VFormTextarea/VFormTextarea.vue';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';

const clickUrl = ref<string>();

const route = useRoute();
const { copyToClipboard } = useCopyToClipboard();
const { isMobile } = useDevice();
const { downloadImage } = useDownloadImageByUrl();

const creativeLink = ref<CreativesLinkTableDetailsItem>();

const previewImgDialog = ref<boolean>(false);
const copyReferralCodeDialog = ref<boolean>(false);

const merchant = computed<number | undefined>(() => (
  route.params.merchant ? Number(route.params.merchant) : undefined
));

const serialId = computed<string | undefined>(() => (
  route.params.serialId ? String(route.params.serialId) : undefined
));

const format = computed<string | undefined>(() => (
  route.params.format ? String(route.params.format) : undefined
));

const language = computed<string | undefined>(() => (
  route.params.language ? String(route.params.language) : undefined
));

const creativeAdvertisingCompanyId = computed<string | undefined>(() => (
  route.params.creativeAdvertisingCompanyId ? String(route.params.creativeAdvertisingCompanyId) : undefined
));

const creativeResolutionId = computed<string | undefined>(() => (
  route.params.creativeResolutionId ? String(route.params.creativeResolutionId) : undefined
));

const {
  data,
  isLoading,
  isFetching,
  tableState,
  updateTableState,
  refreshTableData,
} = useTableData(
  useCreativeLinkListQuery,
  merchant,
  serialId,
  format,
  language,
  creativeAdvertisingCompanyId,
  creativeResolutionId,
);

const columnOptions: ITableColumnsOptions = {
  title: {
    defaultMobilePreview: true,
  },
};

function handleClick(listItem: CreativesLinkTableDetailsItem, event: CreativeLinkHandlerEvents): void {
  creativeLink.value = listItem;
  clickUrl.value = format.value !== 'link' ? creativeLink.value?.url_js : creativeLink.value?.url;

  switch (event) {
    case CreativeLinkHandlerEvents.IMAGE_PREVIEW:
      previewImgDialog.value = true;
      break;
    case CreativeLinkHandlerEvents.REFERRAL_PREVIEW:
      copyReferralCodeDialog.value = true;
      break;
    case CreativeLinkHandlerEvents.REFERRAL_COPY:
      copy(clickUrl.value);
      break;
    case CreativeLinkHandlerEvents.DOWNLOAD_IMAGE:
      void downloadImage(listItem.img, listItem.banner_file_name || listItem.title);
      break;
    default:
      break;
  }
}

function copy(value?: string): void {
  if (value) {
    void copyToClipboard(value);
  }
}

function closeDialog(): void {
  previewImgDialog.value = false;
  copyReferralCodeDialog.value = false;
}
</script>

<template>
  <VCard v-data-qa-auto-id="'CreativesLinkTablePage'" type="table">
    <VTable
      :data="data"
      :title-t-path="$getTPath(TRANSLATION_GROUP_CREATIVES_LINK_TABLE, 'LIST_HEADER')"
      :loading="isLoading"
      :is-fetching="isFetching"
      :state="tableState"
      :columns-options="columnOptions"
      @state-update="updateTableState"
      @refresh="refreshTableData"
    >
      <template #recordAction="{ listItem }">
        <VButton
          v-if="format === 'jpeg' || format === 'png' || format === 'gif'"
          :icon="isMobile ? 'pi pi-download' : undefined"
          :label="isMobile ? undefined : $getTranslation(TRANSLATION_GROUP_CREATIVES_LINK_TABLE, 'DOWNLOAD_IMAGE_BTN')"
          :text="isMobile"
          @click="handleClick(listItem, CreativeLinkHandlerEvents.DOWNLOAD_IMAGE)"
        />
        <VButton
          v-if="format !== 'link'"
          :icon="isMobile ? 'pi pi-eye' : undefined"
          :label="isMobile ? undefined : $getTranslation(TRANSLATION_GROUP_CREATIVES_LINK_TABLE, 'PREVIEW_BTN')"
          :text="isMobile"
          @click="handleClick(listItem, CreativeLinkHandlerEvents.IMAGE_PREVIEW)"
        />
        <VButton
          :icon="isMobile ? 'pi pi-code' : undefined"
          :label="isMobile ? undefined : $getTranslation(TRANSLATION_GROUP_CREATIVES_LINK_TABLE, 'VIEW_CODE_BTN')"
          :text="isMobile"
          @click="handleClick(listItem, CreativeLinkHandlerEvents.REFERRAL_PREVIEW)"
        />
        <VButton
          :icon="isMobile ? 'pi pi-copy' : undefined"
          :label="isMobile ? undefined : $getTranslation(TRANSLATION_GROUP_CREATIVES_LINK_TABLE, 'COPY_CODE_BTN')"
          :text="isMobile"
          @click="handleClick(listItem, CreativeLinkHandlerEvents.REFERRAL_COPY)"
        />
      </template>
      <template
        v-if="$hasPermission(CREATIVES_LINK_PERMISSIONS.SHOW)"
        #footerButtons
      >
        <VBackButton
          :label="$getTranslation(TRANSLATION_GROUP_CREATIVES_LINK_TABLE, 'BACK')"
          :to="{ name: AFFILIATE_CREATIVES_LINK_ROUTE_NAME }"
        />
      </template>
    </VTable>
  </VCard>
  <!-- Preview image dialog -->
  <VDialog
    :visible="previewImgDialog"
    :title="creativeLink?.title"
    @hide="closeDialog"
  >
    <template #default>
      <img
        :src="creativeLink?.img"
        alt="preview"
      >
    </template>
  </VDialog>
  <!-- Copy referral code dialog -->
  <VDialog
    :visible="copyReferralCodeDialog"
    :title="creativeLink?.title"
    @hide="closeDialog"
  >
    <template #default>
      <VFormTextarea
        :label="$getTranslation(TRANSLATION_GROUP_CREATIVES_LINK_TABLE, 'CREATIVE_CODE')"
        :value="clickUrl"
        readonly
      />
    </template>
    <template #footer>
      <VButton
        :label="$getTranslation(TRANSLATION_GROUP_CREATIVES_LINK_TABLE, 'BUTTON_CANCEL')"
        outlined
        @click="closeDialog"
      />
      <VButton
        :label="$getTranslation(TRANSLATION_GROUP_CREATIVES_LINK_TABLE, 'BUTTON_COPY')"
        @click="copy(clickUrl)"
      />
    </template>
  </VDialog>
</template>
